<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { Component } from 'vue';
import LikeIcon from '@/components/icons/LikeIcon.vue';
import DislikeIcon from '@/components/icons/DislikeIcon.vue';
import FilledLikeIcon from '@/components/icons/FilledLikeIcon.vue';
import FilledDislikeIcon from '@/components/icons/FilledDislikeIcon.vue';
import { ArticleRate } from '@/common/api/models.pleased';
import { analytics } from '@/common/logs';
import { BTN_EVTS } from '@/directives/trackClick';
import { useArticleRating, useRateArticle } from '../api/articleRating.api';
import { useI18n } from '@/plugins/i18n';
import {
  usePositiveFeedbackCustomisation,
  useNegativeFeedbackCustomisation,
} from '../composables/useArticleRatingCustomisation';
import { createTextStyle } from '@/common/customisation/utils/styleUtils';

interface Props {
  articleId: number;
}

const props = defineProps<Props>();
const { t } = useI18n();

// Customisation
const positiveFeedbackCustomisation = usePositiveFeedbackCustomisation();
const negativeFeedbackCustomisation = useNegativeFeedbackCustomisation();

// Fetch current rating (only for logged-in users)
const { data: ratingData } = useArticleRating(computed(() => props.articleId));

// Local state for UI (used for both guest and logged-in users)
const currentRating = ref<ArticleRate | null>(null);

// Initialize from API data when it loads (for logged-in users)
watch(
  () => ratingData.value?.rating,
  (newRating) => {
    if (newRating && currentRating.value === null) {
      currentRating.value = newRating;
    }
  },
  { immediate: true }
);

// Mutation for rating the article
const { mutate: rateArticle } = useRateArticle();

interface RatingOptionConfig {
  value: ArticleRate;
  defaultTextKey: string;
  icon: Component;
  filledIcon: Component;
  customisation: ReturnType<typeof usePositiveFeedbackCustomisation>;
}

const createRatingOption = (config: RatingOptionConfig) => {
  const { value, defaultTextKey, icon, filledIcon, customisation } = config;
  return {
    value,
    text: customisation.labelText.value || t(defaultTextKey),
    icon,
    filledIcon,
    textStyle: createTextStyle(customisation.labelColor),
    iconStyle: createTextStyle(customisation.actionColor),
  };
};

const ratingOptions = computed(() => [
  createRatingOption({
    value: ArticleRate.HELPFUL,
    defaultTextKey: 'page.article.feedback.helpful',
    icon: LikeIcon,
    filledIcon: FilledLikeIcon,
    customisation: positiveFeedbackCustomisation,
  }),
  createRatingOption({
    value: ArticleRate.NOT_HELPFUL,
    defaultTextKey: 'page.article.feedback.not_helpful',
    icon: DislikeIcon,
    filledIcon: FilledDislikeIcon,
    customisation: negativeFeedbackCustomisation,
  }),
]);

const handleArticleRate = (rate: ArticleRate) => {
  const eventName = rate === ArticleRate.HELPFUL ? BTN_EVTS.ARTICLE_HELPFUL : BTN_EVTS.ARTICLE_NOT_HELPFUL;
  analytics.trackClick({
    name: eventName,
    selection: props.articleId,
  });

  // Update local state immediately for optimistic UI feedback
  currentRating.value = rate;

  // Call API in background
  rateArticle({
    articleId: props.articleId,
    rating: rate,
  });
};
</script>

<template>
  <div class="article-rate">
    <div
      v-for="option in ratingOptions"
      :key="option.value"
      class="rate-option"
      :class="{ selected: currentRating === option.value }"
      :style="option.textStyle"
      :aria-label="option.text"
      @click="handleArticleRate(option.value)"
    >
      <component :is="currentRating === option.value ? option.filledIcon : option.icon" :style="option.iconStyle" />
      <span>{{ option.text }}</span>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/scss/variable.scss';

.article-rate {
  display: flex;
  gap: 20px;
  margin-top: 8px;
  padding: 0;

  .rate-option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
    font-weight: 500;
    cursor: pointer;
    outline: none;
    -webkit-tap-highlight-color: transparent;
    user-select: none;
    transition: background-color 0.2s ease;

    &:active,
    &:focus {
      outline: none;
    }
  }
}
</style>
